/**
 * SECURITY: All DB access goes through server-side API - NO getServiceRoleClient() in browser
 */
import { getSessionHeaders } from '../serverSession';

async function callSponsorNotifyAPI(operation: string, params: any = {}): Promise<any> {
 const response = await fetch('/api/admin/sponsor-change-notify', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, ...params }),
 });
 if (!response.ok) {
 const errorData = await response.json().catch(() => ({}));
 throw new Error(errorData.error || `API error: ${response.status}`);
 }
 return response.json();
}

interface SponsorChangeNotification {
 userId: number;
 oldSponsorId: number | null;
 newSponsorId: number;
 usdtTransferred: number;
 sharesTransferred: number;
 reason: string;
 adminEmail: string;
}

/**
 * Send notifications to both old and new sponsors about commission transfer
 */
export async function notifySponsorsOfCommissionTransfer(params: SponsorChangeNotification): Promise<void> {
 try {
 // Get all user details in one API call
 const { user, oldSponsor, newSponsor } = await callSponsorNotifyAPI('get_users_for_notification', {
 userId: params.userId,
 oldSponsorId: params.oldSponsorId,
 newSponsorId: params.newSponsorId,
 });

 if (!user) {
 console.error('User not found for notification');
 return;
 }

 const userName = user.first_name && user.last_name
 ? `${user.first_name} ${user.last_name}`
 : user.username;

 // Notify OLD sponsor (commissions deducted)
 if (params.oldSponsorId && oldSponsor) {
 await createNotification({
 userId: params.oldSponsorId,
 title: ' Commission Adjustment - Sponsor Change',
 message: `Commissions from ${userName} have been transferred due to sponsor change. USDT: $${params.usdtTransferred.toFixed(2)}, Shares: ${params.sharesTransferred.toFixed(2)}. Reason: ${params.reason}`,
 type: 'commission_adjustment',
 metadata: {
 affected_user_id: params.userId,
 affected_user_name: userName,
 usdt_deducted: params.usdtTransferred,
 shares_deducted: params.sharesTransferred,
 reason: params.reason,
 admin: params.adminEmail
 }
 });

 if (oldSponsor.email) {
 await sendSponsorChangeEmail({
 recipientEmail: oldSponsor.email,
 recipientName: oldSponsor.full_name || oldSponsor.username,
 type: 'deduction',
 affectedUserName: userName,
 usdtAmount: params.usdtTransferred,
 sharesAmount: params.sharesTransferred,
 reason: params.reason
 });
 }

 console.log(`Notification sent to old sponsor (User ${params.oldSponsorId})`);
 }

 // Notify NEW sponsor (commissions credited)
 if (newSponsor) {
 await createNotification({
 userId: params.newSponsorId,
 title: ' Commission Credit - New Referral',
 message: `You've received commissions from ${userName} due to sponsor change. USDT: $${params.usdtTransferred.toFixed(2)}, Shares: ${params.sharesTransferred.toFixed(2)}. Welcome to your new team member!`,
 type: 'commission_credit',
 metadata: {
 new_referral_id: params.userId,
 new_referral_name: userName,
 usdt_credited: params.usdtTransferred,
 shares_credited: params.sharesTransferred,
 reason: params.reason,
 admin: params.adminEmail
 }
 });

 if (newSponsor.email) {
 await sendSponsorChangeEmail({
 recipientEmail: newSponsor.email,
 recipientName: newSponsor.full_name || newSponsor.username,
 type: 'credit',
 affectedUserName: userName,
 usdtAmount: params.usdtTransferred,
 sharesAmount: params.sharesTransferred,
 reason: params.reason
 });
 }

 console.log(`Notification sent to new sponsor (User ${params.newSponsorId})`);
 }

 } catch (error) {
 console.error('Error sending sponsor change notifications:', error);
 // Don't throw - notifications are non-critical
 }
}

/**
 * Create a notification record in the database via API
 */
async function createNotification(params: {
 userId: number;
 title: string;
 message: string;
 type: string;
 metadata?: any;
}): Promise<void> {
 try {
 await callSponsorNotifyAPI('create_notification', {
 userId: params.userId,
 title: params.title,
 message: params.message,
 type: params.type,
 metadata: params.metadata,
 });
 } catch (error) {
 console.error('Error in createNotification:', error);
 }
}

/**
 * Send email notification for sponsor change via API proxy
 */
export async function sendSponsorChangeEmail(params: {
 recipientEmail: string;
 recipientName: string;
 type: 'deduction' | 'credit';
 affectedUserName: string;
 usdtAmount: number;
 sharesAmount: number;
 reason?: string;
}): Promise<void> {
 try {
 console.log('Sending sponsor change email notification:', {
 to: params.recipientEmail,
 type: params.type
 });

 const response = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 emailType: 'sponsor_change',
 data: {
 email: params.recipientEmail,
 userName: params.recipientName,
 type: params.type,
 affectedUserName: params.affectedUserName,
 usdtAmount: params.usdtAmount,
 sharesAmount: params.sharesAmount,
 reason: params.reason
 }
 })
 });

 const result = await response.json();

 if (!response.ok || !result.success) {
 console.error('Failed to send sponsor change email:', result.error);
 } else {
 console.log('Sponsor change email sent successfully:', result.messageId);
 }
 } catch (error) {
 console.error('Error sending sponsor change email:', error);
 }
}

